import { formatDateDisplay } from '../utils/date';
import { getScheduleShort, getProgramTypeIcon, programNameToSlug, getProgramTypeGlyph, getProgramTypeColor, isEnrollmentLocked } from '../types/program';
import { getBillingTypeDisplay } from '../utils/billingDisplay';
import { splitFlexPricing } from '../utils/flexPlan';
import { findEligibilityDiscount, isEligibilityDiscountSubtype } from '../utils/eligibilityDiscounts';
import { SectionGlyph } from './SectionGlyph';
import { useLabels, useBillingTypeLabel } from '../config/useLabels';
import { useBrandProfile } from '../contexts/brand-profile';
import { brandProfileOf } from '../config/brandProfiles';
import { BrandBadge } from './BrandBadge';
import { LocationPill } from './LocationPill';
import { programLocationName, type WithProgramLocation } from '../types/location';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { Heart, Star, GraduationCap, MapPin, Users, Sparkles, Clock, TrendingUp, Lock, type LucideIcon } from 'lucide-react';

type ReasonType = 'enrolled' | 'interest' | 'grade' | 'school' | 'similar' | 'new' | 'starting-soon' | 'popular';

const REASON_CONFIG: Record<ReasonType, { Icon: LucideIcon }> = {
  enrolled:        { Icon: Heart          },
  interest:        { Icon: Star           },
  grade:           { Icon: GraduationCap  },
  school:          { Icon: MapPin         },
  similar:         { Icon: Users          },
  new:             { Icon: Sparkles       },
  'starting-soon': { Icon: Clock          },
  popular:         { Icon: TrendingUp     },
};

interface LineItemConfig {
  id: string;
  name: string;
  type?: string;
  description?: string;
  subtype?: string;
}
interface PricingItem {
  amount: number;
  is_optional: boolean;
  line_item_configs: LineItemConfig | LineItemConfig[];
}
interface Program extends WithProgramLocation {
  id: string;
  name: string;
  type: string;
  /** Renders the brand pill. Absent simply omits it — see BrandBadge. */
  brand_key?: string | null;
  price: number;
  billing_type: string;
  schedule_cron: string;
  start_date: string;
  end_date: string;
  /** 'closed' means the program still runs but takes no new students. Absent reads as open. */
  enrollment_status?: string | null;
  pricing?: PricingItem[];
}
interface ProgramCardProps {
  program: Program;
  onClick?: () => void;
  buttonText?: string;
  reasonLabel?: string;
  reasonType?: ReasonType;
  reasonExpanded?: boolean;
  onReasonToggle?: () => void;
  /** Capped and no seat left (BHCS-124). The card stays; its page offers the waitlist. */
  isFull?: boolean;
}

// Deprecated local alias; use getProgramTypeIcon directly

const getPricingBreakdown = (program: Program) => {
  // Flex monthly bills a per-day rate for confirmed days, plus any one-time materials
  // (first month only). Showing the naive sum as one total (e.g. $340) is misleading, so
  // split it into the per-day rate and the one-time add-on — mirrors ProgramDetails.
  if (program.pricing && program.billing_type === 'flex_monthly') {
    const rows = program.pricing.map((pricing: PricingItem) => {
      const c = Array.isArray(pricing.line_item_configs)
        ? pricing.line_item_configs[0]
        : pricing.line_item_configs;
      return { id: c?.id, amount: pricing.amount, type: c?.type, subtype: c?.subtype };
    });
    const { perDayRate, monthlyAddOns: flatAddOns } = splitFlexPricing(rows);
    // includeMaterials folds one-time materials into the flat bucket; the difference from the
    // no-materials add-ons isolates the one-time materials amount (first month only).
    const withMaterials = splitFlexPricing(rows, { includeMaterials: true }).monthlyAddOns;
    const oneTimeAmount = withMaterials - flatAddOns;
    return {
      isFlex: true,
      perDayRate,
      monthlyAddOns: flatAddOns,
      oneTimeAmount,
      total: program.price,
      isMonthly: false,
      monthlyAmount: 0,
      eligibilityDiscount: null,
      discountedTotal: program.price,
    };
  }
  if (!program.pricing || program.billing_type !== 'recurring_monthly') {
    // For non-recurring programs, check for an eligibility discount (BHCS Student or
    // Foster City Resident). A program carries at most one.
    let discountAmount = 0;
    let baseTotal = 0;
    if (program.pricing) {
      program.pricing.forEach((pricing: PricingItem) => {
        const lineItemConfig = Array.isArray(pricing.line_item_configs)
          ? pricing.line_item_configs[0]
          : pricing.line_item_configs;
        if (isEligibilityDiscountSubtype(lineItemConfig?.subtype)) {
          discountAmount += pricing.amount;
        } else if (!pricing.is_optional) {
          baseTotal += pricing.amount;
        }
      });
    }
    const eligibilityDiscount = findEligibilityDiscount(
      (program.pricing ?? []).map((pricing: PricingItem) => {
        const c = Array.isArray(pricing.line_item_configs) ? pricing.line_item_configs[0] : pricing.line_item_configs;
        return c?.subtype;
      })
    );
    // Use computed base (excluding optional) when pricing data is available, else fall back to program.price
    const total = program.pricing ? baseTotal : program.price;
    return {
      isFlex: false,
      perDayRate: 0,
      monthlyAddOns: 0,
      total,
      isMonthly: false,
      monthlyAmount: 0,
      oneTimeAmount: 0,
      eligibilityDiscount,
      discountedTotal: total + discountAmount,
    };
  }
  let monthlyAmount = 0;
  let oneTimeAmount = 0;
  program.pricing.forEach((pricing: PricingItem) => {
    const lineItemConfig = Array.isArray(pricing.line_item_configs)
      ? pricing.line_item_configs[0]
      : pricing.line_item_configs;
    if (pricing.is_optional) return;
    if (lineItemConfig?.type === 'materials') {
      oneTimeAmount += pricing.amount;
    } else {
      monthlyAmount += pricing.amount;
    }
  });
  return {
    isFlex: false,
    perDayRate: 0,
    monthlyAddOns: 0,
    monthlyAmount,
    oneTimeAmount,
    total: monthlyAmount + oneTimeAmount,
    isMonthly: true,
    eligibilityDiscount: null,
    discountedTotal: monthlyAmount + oneTimeAmount,
  };
};

const formatPricing = (program: Program) => {
  const breakdown = getPricingBreakdown(program);
  if (breakdown.isFlex) {
    const parts = [`$${breakdown.perDayRate.toFixed(2)}/day`];
    if (breakdown.monthlyAddOns > 0) parts.push(`$${breakdown.monthlyAddOns.toFixed(2)}/mo`);
    if (breakdown.oneTimeAmount > 0) parts.push(`$${breakdown.oneTimeAmount.toFixed(2)} one-time`);
    return parts.join(' + ');
  }
  if (!breakdown.isMonthly || breakdown.oneTimeAmount === 0) {
    const basePrice = `$${breakdown.total.toFixed(2)}`;
    return program.billing_type === 'per_day' ? `${basePrice}/day` : basePrice;
  }
  return `$${breakdown.monthlyAmount.toFixed(2)} + $${breakdown.oneTimeAmount.toFixed(2)}`;
};

/** The discounted price line, e.g. "BHCS Student: $675.00", or null without a discount. */
const formatDiscountedPrice = (program: Program) => {
  const breakdown = getPricingBreakdown(program);
  if (!breakdown.eligibilityDiscount) return null;
  const suffix = program.billing_type === 'per_day' ? '/day' : '';
  return `${breakdown.eligibilityDiscount.holderLabel}: $${breakdown.discountedTotal.toFixed(2)}${suffix}`;
};

export const ProgramCard: React.FC<ProgramCardProps> = ({ program, onClick, buttonText, reasonLabel, reasonType, reasonExpanded, onReasonToggle, isFull = false }) => {
  const navigate = useNavigate();
  const t = useLabels();
  const billingLabel = useBillingTypeLabel();
  // The corner glyph is decorative CJK, and unlike the labels it is NOT gated by the
  // bilingual flag -- it renders on BHCS either way. It still has to respect a brand that
  // is English-only, so it reads the brand's own language property rather than the flag.
  // Admin surfaces render this card outside BrandProfileProvider and so get the default
  // brand, which keeps its glyph.
  const { activeBrand } = useBrandProfile();
  const showGlyph = brandProfileOf(activeBrand).bilingual;
  // The card advertises the program, so the discounted price (BHCS Student or Foster City
  // Resident) is always shown when the program has the discount — eligibility is resolved
  // later, per student, at enrollment.
  const discountedPrice = formatDiscountedPrice(program);
  const enrollmentLocked = isEnrollmentLocked(program);
  const locationName = programLocationName(program);
  // Default CTA is compound; only the verb is bilingual ("Learn More | 报名 Enroll").
  // A locked program drops the verb but keeps the card navigable — the detail page is still worth
  // reading (schedule, price, description), it just can't be enrolled into. A full one says what
  // its page offers instead of an enrollment it would refuse.
  const resolvedButtonText = buttonText
    ?? (enrollmentLocked
      ? 'Learn More'
      : `Learn More | ${isFull ? t('action.joinWaitlist') : t('action.enrollShort')}`);
  const reasonConfig = reasonType ? REASON_CONFIG[reasonType] : null;
  return (
    <div
      className="relative bg-white rounded-2xl shadow overflow-hidden flex flex-col h-full w-full"
      onClick={() => { if (reasonConfig && reasonLabel) onReasonToggle?.(); }}
    >
      {/* Decorative type glyph, bleeding off the top-right corner */}
      {showGlyph && (
        <SectionGlyph
          char={getProgramTypeGlyph(program.type)}
          className="absolute -top-3 -right-1 text-5xl sm:text-6xl pointer-events-none z-0"
          style={{ color: getProgramTypeColor(program.type), opacity: 0.1 }}
        />
      )}
      <div className="relative z-10 p-3 sm:p-4 flex flex-col h-full">
      {reasonConfig && reasonLabel && reasonExpanded && (
        <div className="inline-flex items-center gap-1 rounded-full border border-[color:var(--brand-accent-edge)] bg-[var(--brand-accent-soft)] px-2 py-0.5 text-xs font-normal text-[color:var(--brand-accent)] mb-1.5">
          <reasonConfig.Icon className="h-2.5 w-2.5 flex-shrink-0" />
          <span className="leading-tight">{reasonLabel}</span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-base flex-shrink-0" style={{ lineHeight: 1 }}>{getProgramTypeIcon(program.type)}</span>
        <span
          className="text-sm sm:text-base text-gray-900 leading-tight break-words min-w-0 flex-1"
          style={{ fontWeight: 'var(--brand-name-weight)' }}
        >
          {program.name}
        </span>
        {reasonConfig && reasonLabel && !reasonExpanded && (
          <reasonConfig.Icon className="h-3 w-3 flex-shrink-0 text-[color:var(--brand-accent)]" />
        )}
      </div>
      <hr className="border-gray-100 mb-1.5" />
      {/* One wrapped row rather than two: the billing type, the days and the term are all the
          same kind of fact, and a card this narrow was spending a line break between them. */}
      <div className="flex flex-wrap gap-1.5 mb-2">
        <BrandBadge brand={program.brand_key} size="xs" />
        <LocationPill name={locationName} />
        <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
          {(() => { const { Icon } = getBillingTypeDisplay(program.billing_type); return <><Icon className="w-3 h-3" />{billingLabel(program.billing_type)}</>; })()}
        </span>
        {enrollmentLocked && (
          <span className="inline-flex items-center gap-1 text-xs border border-amber-300 bg-amber-50 text-amber-800 rounded-full px-2 py-0.5">
            <Lock className="w-3 h-3" />{t('status.enrollmentClosed')}
          </span>
        )}
        {isFull && !enrollmentLocked && (
          <span className="inline-flex items-center gap-1 text-xs border border-amber-300 bg-amber-50 text-amber-800 rounded-full px-2 py-0.5">
            <Users className="w-3 h-3" />{t('status.full')}
          </span>
        )}
        <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 leading-tight">
          {getScheduleShort(program.schedule_cron)}
        </span>
        <span className="inline-flex items-center text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 leading-tight whitespace-nowrap">
          {formatDateDisplay(program.start_date)} – {formatDateDisplay(program.end_date)}
        </span>
      </div>
      <div className="text-sm font-semibold text-gray-900 mb-1">
        {formatPricing(program)}
      </div>
      {discountedPrice && (
        <div className="text-xs font-medium text-green-700 mb-1">
          {discountedPrice}
        </div>
      )}
      {program.billing_type === 'recurring_monthly' && (() => {
        const breakdown = getPricingBreakdown(program);
        return breakdown.oneTimeAmount > 0;
      })() && (
        <div className="text-[11px] text-gray-500 mb-3 leading-tight">
          Monthly + one-time (first month only)
        </div>
      )}
      {program.billing_type === 'flex_monthly' && (() => {
        const breakdown = getPricingBreakdown(program);
        return breakdown.oneTimeAmount > 0;
      })() && (
        <div className="text-[11px] text-gray-500 mb-3 leading-tight">
          Billed per day + one-time (first month only)
        </div>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          (onClick || (() => {
            const programSlug = programNameToSlug(program.name);
            navigate(`/programs/${programSlug}`);
          }))();
        }}
        className="brand-cta brand-cta-card mt-auto w-full text-white transition-colors break-words"
      >
        {resolvedButtonText}
      </button>
      </div>
    </div>
  );
}; 